import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getCapsulas, getPersonasMant, detenerCapsula, deleteCapsula } from '../../api/client'
import { useAsignar } from './useAsignar'
import { useReloj } from './useReloj'
import { duracion, hora, resumir, segundosVivos, enColacion } from './tiempo'
import ListaCapsulas from './ListaCapsulas'
import RegistrarModal from './RegistrarModal'
import PersonaTrabajo, { Cifra, Barra } from './PersonaTrabajo'
import { usePuedeEditarMantenimiento, usePuedeAsignarPartes } from '../../utils/mantenimiento'

// A parte's "Trabajo" tab: who is on it now, total time and time per person,
// and every capsule worked on it. An open parte takes people from here too
// (for those who may edit Mantenimiento).
export default function CapsulasDeParte({ parte }) {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeEditarMantenimiento()     // delete a record: Mantenimiento's own role
  const puedeAsignar = usePuedeAsignarPartes()           // start, pause, register: also whoever holds asignar_personas
  const ahora = useReloj()
  const [caps, setCaps] = useState(null)
  const [recibido, setRecibido] = useState(0)
  const [personas, setPersonas] = useState([])
  const [error, setError] = useState('')
  const [registrando, setRegistrando] = useState(false)
  const [viendo, setViendo] = useState(null)

  const cargar = useCallback(() => {
    getCapsulas({ parte_id: parte.id })
      .then(cs => { setCaps(cs); setRecibido(Date.now()); setError('') })
      .catch(() => setError(t('capsulas.cargaFallo')))
  }, [parte.id, t])
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => { getPersonasMant().then(setPersonas).catch(() => {}) }, [])

  const { asignar, ui } = useAsignar({ onHecho: cargar })
  const pausar = async (c) => { try { await detenerCapsula(c.id) } finally { cargar() } }
  const borrar = async (c) => {
    if (!window.confirm(t('capsulas.borrarConfirm', { persona: c.persona_nombre, duracion: duracion(c.segundos) }))) return
    try { await deleteCapsula(c.id) } finally { cargar() }
  }

  if (error) return <p className="text-red-500 text-sm">{error}</p>
  if (!caps) return <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>

  const vivas = caps.map(c => ({ ...c, segundos: segundosVivos(c, recibido, ahora) }))
  const r = resumir(vivas)
  const ahoraEn = vivas.filter(c => c.abierta)
  const libres = personas.filter(p => !ahoraEn.some(c => c.persona_id === p.id))

  return (
    <div className="h-full overflow-y-auto space-y-5 pr-1">
      {puedeAsignar && <div className="flex flex-wrap items-center gap-2">
        {parte.abierto && (
          <select value="" onChange={e => { const p = libres.find(x => String(x.id) === e.target.value); if (p) asignar(p, parte) }}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="">{t('capsulas.asignarPersona')}</option>
            {libres.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        )}
        <button type="button" onClick={() => setRegistrando(true)}
          className="text-sm font-medium text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
          {t('capsulas.registrar.boton')}
        </button>
      </div>}

      {ahoraEn.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ahoraEn.map(c => (
            <span key={c.id} className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-0.5 text-xs ${
              c.hora_extra ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'}`}>
              ● <span className="font-medium">{c.persona_nombre}</span>
              <span className="tabular-nums opacity-75">
                {enColacion(caps.find(x => x.id === c.id), recibido, ahora) ? t('capsulas.retomaCorto', { hora: hora(c.inicio) }) : duracion(c.segundos)}
              </span>
              {puedeAsignar && (
                <button type="button" onClick={() => pausar(c)} title={t('capsulas.pausar')}
                  className="w-4 h-4 rounded-full hover:bg-black/10 leading-none">×</button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-4 gap-2">
        <Cifra etiqueta={t('capsulas.total')} valor={duracion(r.total.seg)} fuerte />
        <Cifra etiqueta={t('capsulas.normales')} valor={duracion(r.total.normal)} />
        <Cifra etiqueta={t('capsulas.extras')} valor={duracion(r.total.extra)} ambar={r.total.extra > 0} />
        <Cifra etiqueta={t('capsulas.personas')} valor={r.porPersona.length} />
      </div>

      {r.porPersona.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('capsulas.porPersona')}</h3>
          <ul className="space-y-1">
            {r.porPersona.map(p => (
              <li key={p.persona_id}>
                <button type="button" onClick={() => setViendo(p.persona_id)} title={t('capsulas.verTrabajo')}
                  className="w-full flex items-center gap-3 text-left rounded-lg px-2 py-1.5 hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900 flex-1 truncate">{p.nombre}</span>
                  {p.extra > 0 && <span className="text-[11px] text-amber-700">{t('capsulas.extraCorto', { d: duracion(p.extra) })}</span>}
                  <Barra valor={p.seg} max={r.porPersona[0].seg} extra={p.extra} />
                  <span className="text-sm font-semibold tabular-nums w-20 text-right">{duracion(p.seg)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('capsulas.historial')}</h3>
        <ListaCapsulas caps={caps} mostrar="persona" recibido={recibido} ahora={ahora}
          onPersona={setViendo} onDetener={puedeAsignar ? pausar : undefined} onBorrar={puedeEditar ? borrar : undefined} />
      </section>

      {registrando && (
        <RegistrarModal personas={personas} parte={parte} onClose={() => setRegistrando(false)} onHecho={cargar} />
      )}
      {viendo != null && <PersonaTrabajo key={viendo} personaId={viendo} onClose={() => setViendo(null)} />}
      {ui}
    </div>
  )
}
